<template>
  <!-- 维护模式全屏覆盖层 -->
  <div v-if="maintenanceMode" class="maintenance-overlay">
    <div class="maintenance-card">
      <div class="maintenance-icon">
        <i class="fas fa-tools"></i>
      </div>
      <h2 class="maintenance-title">系统维护中</h2>
      <p class="maintenance-message">{{ maintenanceMessage }}</p>
      <p class="maintenance-hint">服务器正在维护中，开放时间请关注官方公告</p>
      <p v-if="retryStatus" class="maintenance-retry-status" :class="{ 'retry-success': retrySuccess, 'retry-fail': !retrySuccess }">
        <i :class="retrySuccess ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
        {{ retryStatus }}
      </p>
      <button v-if="!retrySuccess" class="maintenance-retry-btn" @click="retryConnection" :disabled="retrying">
        <i class="fas fa-redo" :class="{ 'fa-spin': retrying }"></i>
        {{ retrying ? '检测中...' : '重试连接' }}
      </button>
    </div>
  </div>

  <!-- 申请页面不显示header和侧边栏 -->
  <div v-if="route.name === 'application'" class="application-only-container">
    <router-view></router-view>
  </div>
  
  <!-- 未登录用户显示简化界面 -->
  <div v-else-if="!isLoggedIn" class="public-container">
    <!-- 顶部导航栏 -->
    <header class="top-header">
      <div class="header-left">
        <button class="menu-toggle d-lg-none" @click="toggleSidebar">
          <i class="fas fa-bars fa-lg"></i>
        </button>
      </div>
      <div class="header-right">
        <!-- 未登录用户显示登录按钮 -->
        <router-link to="/login" class="login-btn-header">
          <i class="fas fa-sign-in-alt"></i>
          登录
        </router-link>
      </div>
    </header>

    <!-- 简化的侧边栏 -->
    <aside class="sidebar" :class="{ 'sidebar-visible': isSidebarVisible }">
      <div class="sidebar-header">
        <div class="sidebar-header-content">
          <a href="/" class="logo">
            <img :src="logoUrl" alt="Logo" class="logo-img">
          </a>
          <!-- 移动端关闭按钮，只在侧边栏打开时显示 -->
          <button v-if="isSidebarVisible" class="sidebar-close d-lg-none" @click="closeSidebar">
            <i class="fas fa-times fa-lg"></i>
          </button>
        </div>
      </div>

      <nav class="sidebar-nav">
        <!-- 未登录用户只显示首页和登录 -->
        <router-link to="/" class="nav-item" active-class="active" @click="closeSidebar">
          <div class="nav-item-content">
            <i class="fas fa-home fa-lg"></i>
            <span>首页</span>
          </div>
        </router-link>

        <router-link to="/login" class="nav-item" active-class="active" @click="closeSidebar">
          <div class="nav-item-content">
            <i class="fas fa-sign-in-alt fa-lg"></i>
            <span>登录</span>
          </div>
        </router-link>
      </nav>

      <!-- 未登录用户不显示用户菜单 -->
    </aside>

    <!-- 移动端遮罩层 -->
    <div v-if="isSidebarVisible" class="sidebar-overlay d-lg-none" @click="closeSidebar"></div>

    <!-- 主内容区 -->
    <main class="main-content">
      <router-view></router-view>
    </main>
  </div>
  
  <!-- 已登录用户显示完整界面 -->
  <div v-else class="app-wrapper">
    <!-- 顶部导航栏 -->
    <header class="top-header">
      <div class="header-left">
        <button class="menu-toggle d-lg-none" @click="toggleSidebar">
          <i class="fas fa-bars fa-lg"></i>
        </button>
      </div>
      <div class="header-right">
        <!-- 这里原本有语言切换按钮，已移除 -->
      </div>
    </header>

    <!-- 侧边栏 -->
    <aside class="sidebar" :class="{ 'sidebar-visible': isSidebarVisible }">
      <div class="sidebar-header">
        <div class="sidebar-header-content">
          <a href="/" class="logo">
            <img :src="logoUrl" alt="Logo" class="logo-img">
          </a>
          <!-- 移动端关闭按钮，只在侧边栏打开时显示 -->
          <button v-if="isSidebarVisible" class="sidebar-close d-lg-none" @click="closeSidebar">
            <i class="fas fa-times fa-lg"></i>
          </button>
        </div>
      </div>

      <nav class="sidebar-nav">
        <router-link to="/" class="nav-item" active-class="active" @click="closeSidebar">
          <div class="nav-item-content">
            <i class="fas fa-home fa-lg"></i>
            <span>首页</span>
          </div>
        </router-link>

        <!-- 只有atc_level > 0的用户才能看到这些菜单 -->
        <template v-if="userAtcLevelComputed > 0">
          <!-- 活动中心菜单（所有atc_level > 0用户可见） -->
          <router-link to="/events" class="nav-item" active-class="active" @click="closeSidebar">
            <div class="nav-item-content">
              <i class="fas fa-calendar fa-lg"></i>
              <span>活动中心</span>
            </div>
          </router-link>

          <!-- 积分商城 -->
          <router-link to="/points-shop" class="nav-item" active-class="active" @click="closeSidebar">
            <div class="nav-item-content">
              <i class="fas fa-store fa-lg"></i>
              <span>积分商城</span>
            </div>
          </router-link>

          <!-- 我的报名菜单 -->
          <router-link to="/events/my-applications" class="nav-item" active-class="active" @click="closeSidebar">
            <div class="nav-item-content">
              <i class="fas fa-clipboard-list fa-lg"></i>
              <span>我的报名</span>
            </div>
          </router-link>

          <!-- 活动管理菜单（仅有权限者可见） -->
          <div v-if="userEventAdmin" class="nav-item has-submenu" 
               :class="{ 'active': isEventsMenuOpen || isInEventsSection }">
            <div class="nav-item-content" @click="toggleEventsMenu">
              <i class="fas fa-calendar-alt fa-lg"></i>
              <span>活动管理</span>
              <i class="fas fa-chevron-down submenu-arrow" 
                 :class="{ 'rotated': isEventsMenuOpen }"></i>
            </div>
            <div class="submenu" :class="{ 'show': isEventsMenuOpen || isInEventsSection }">
              <router-link to="/events/list" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-list"></i>
                <span>活动列表</span>
              </router-link>
              <router-link to="/events/publish" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-plus"></i>
                <span>发布活动</span>
              </router-link>
            </div>
          </div>

          <!-- 空管学院下拉菜单 -->
          <div class="nav-item has-submenu" 
               :class="{ 'active': isAcademyMenuOpen || isInAcademySection }">
            <div class="nav-item-content" @click="toggleAcademyMenu">
              <i class="fas fa-university fa-lg"></i>
              <span>空管学院</span>
              <i class="fas fa-chevron-down submenu-arrow" 
                 :class="{ 'rotated': isAcademyMenuOpen }"></i>
            </div>
            <div class="submenu" :class="{ 'show': isAcademyMenuOpen || isInAcademySection }">
              <router-link to="/academy/exam" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-file-alt"></i>
                <span>考试中心</span>
              </router-link>
              <router-link to="/academy/records" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-clipboard-list"></i>
                <span>考核记录</span>
              </router-link>
              <router-link to="/academy/software" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-download"></i>
                <span>软件下载</span>
              </router-link>
            </div>
          </div>

          <!-- 职业生涯下拉菜单 -->
          <div class="nav-item has-submenu" 
               :class="{ 'active': isCareerMenuOpen || isInCareerSection }">
            <div class="nav-item-content" @click="toggleCareerMenu">
              <i class="fas fa-briefcase fa-lg"></i>
              <span>职业生涯</span>
              <i class="fas fa-chevron-down submenu-arrow" 
                 :class="{ 'rotated': isCareerMenuOpen }"></i>
            </div>
            <div class="submenu" :class="{ 'show': isCareerMenuOpen || isInCareerSection }">
              <router-link to="/career" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-chart-line"></i>
                <span>职业概览</span>
              </router-link>
              <router-link to="/career/duty" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-tasks"></i>
                <span>值班通知</span>
              </router-link>
              <router-link to="/career/duty-records" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-clipboard-check"></i>
                <span>值班记录</span>
              </router-link>
              <router-link to="/career/control-records" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-headset"></i>
                <span>管制记录</span>
              </router-link>
              <router-link to="/career/penalties" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-exclamation-triangle"></i>
                <span>处罚记录</span>
              </router-link>
              <router-link to="/career/trainings" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-graduation-cap"></i>
                <span>培训记录</span>
              </router-link>
              <router-link to="/career/certificates" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-certificate"></i>
                <span>证书管理</span>
              </router-link>
            </div>
          </div>

          <!-- 管理下拉菜单（仅有管理权限或教员权限者可见） -->
          <div v-if="userAdmin || userInstructor" class="nav-item has-submenu" 
               :class="{ 'active': isAdminMenuOpen || isInAdminSection }">
            <div class="nav-item-content" @click="toggleAdminMenu">
              <i class="fas fa-cogs fa-lg"></i>
              <span>管理</span>
              <i class="fas fa-chevron-down submenu-arrow" 
                 :class="{ 'rotated': isAdminMenuOpen }"></i>
            </div>
            <div class="submenu" :class="{ 'show': isAdminMenuOpen || isInAdminSection }">
              <!-- 面试申请审核：有管理权限或教员权限都可见 -->
              <router-link v-if="userAdmin || userInstructor" to="/admin" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-clipboard-check"></i>
                <span>面试申请审核</span>
                <el-badge v-if="unreadCount > 0" :value="unreadCount" class="unread-badge" />
              </router-link>
              <!-- 公告管理：仅系统管理员可见 -->
              <router-link v-if="userSystemAdmin" to="/admin/announcements" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-bullhorn"></i>
                <span>公告管理</span>
              </router-link>
              <!-- 权限管理：仅人员管理员/系统管理员可见 -->
              <router-link v-if="userMemberAdmin" to="/permissions" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-shield-alt"></i>
                <span>权限管理</span>
              </router-link>
              <!-- 学员权限管理：仅教员可见 -->
              <router-link v-if="userInstructor" to="/student-permissions" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-user-graduate"></i>
                <span>学员权限管理</span>
              </router-link>
              <!-- 徽章管理：仅教员可见 -->
              <router-link v-if="userInstructor" to="/admin/badges" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-medal"></i>
                <span>徽章管理</span>
              </router-link>
              <!-- 徽章分配管理：仅系统管理员可见 -->
              <router-link v-if="userSystemAdmin" to="/admin/badge-assignment" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-user-plus"></i>
                <span>徽章分配管理</span>
              </router-link>
              <!-- 积分管理：仅系统管理员可见 -->
              <router-link v-if="userSystemAdmin" to="/admin/points" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-coins"></i>
                <span>积分管理</span>
              </router-link>
              <!-- 软件管理：仅系统管理员可见 -->
              <router-link v-if="userSystemAdmin" to="/admin/software-management" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-download"></i>
                <span>软件管理</span>
              </router-link>
              <!-- 题库管理：仅教员可见 -->
              <router-link v-if="userInstructor" to="/admin/question-bank" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-book"></i>
                <span>题库管理</span>
              </router-link>
              <!-- 智能组卷：仅教员可见 -->
              <router-link v-if="userInstructor" to="/admin/paper-builder" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-magic"></i>
                <span>智能组卷</span>
              </router-link>
              <!-- 学员考试管理：仅教员可见 -->
              <router-link v-if="userInstructor" to="/admin/exam-management" class="submenu-item" active-class="active" @click="closeSidebar">
                <i class="fas fa-graduation-cap"></i>
                <span>学员考试管理</span>
              </router-link>
            </div>
          </div>

          <router-link to="/training" class="nav-item" active-class="active" @click="closeSidebar">
            <div class="nav-item-content">
              <i class="fas fa-graduation-cap fa-lg"></i>
              <span>培训</span>
            </div>
          </router-link>

          <router-link to="/forum" class="nav-item" active-class="active" @click="closeSidebar">
            <div class="nav-item-content">
              <i class="fas fa-comments fa-lg"></i>
              <span>论坛</span>
            </div>
          </router-link>

          <router-link to="/profile" class="nav-item" active-class="active" @click="closeSidebar">
            <div class="nav-item-content">
              <i class="fas fa-user-circle fa-lg"></i>
              <span>个人中心</span>
            </div>
          </router-link>
        </template>

        <!-- atc_level为0的用户显示申请页面和个人中心 -->
        <template v-else-if="userAtcLevelComputed === 0 && isLoggedIn">
          <router-link to="/application" class="nav-item" active-class="active" @click="closeSidebar">
            <div class="nav-item-content">
              <i class="fas fa-user-plus fa-lg"></i>
              <span>管制员申请</span>
            </div>
          </router-link>

          <router-link to="/profile" class="nav-item" active-class="active" @click="closeSidebar">
            <div class="nav-item-content">
              <i class="fas fa-user-circle fa-lg"></i>
              <span>个人中心</span>
            </div>
          </router-link>
        </template>
      </nav>

      <div class="sidebar-footer">
        <div class="user-menu">
          <div class="user-info" @click="toggleUserMenu">
            <img :src="userAvatar" alt="用户头像" class="user-avatar">
            <span class="username">{{ displayName }}</span>
            <i class="fas fa-chevron-down"></i>
          </div>
          <div v-if="showUserMenu" class="user-dropdown">
            <div class="dropdown-item" @click="goToProfile">
              <i class="fas fa-user fa-fw"></i>
              个人中心
            </div>
            <div class="dropdown-item" @click="goToSettings">
              <i class="fas fa-cog fa-fw"></i>
              设置
            </div>
            <div class="dropdown-divider"></div>
            <div class="dropdown-item logout-item" @click="handleLogout">
              <i class="fas fa-sign-out-alt fa-fw"></i>
              退出登录
            </div>
          </div>
        </div>
      </div>
    </aside>

    <!-- 移动端遮罩层 -->
    <div v-if="isSidebarVisible" class="sidebar-overlay d-lg-none" @click="closeSidebar"></div>

    <!-- 主内容区 -->
    <main class="main-content">
      <router-view></router-view>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { pilotAuthAPI } from './api/index'
import { getPilotPermissionsByUidOrCallsign } from './api/pilot-permissions'
import websocketClient from './utils/websocket'
import { maintenanceMode, maintenanceMessage, setMaintenanceMode } from './utils/maintenance'

const route = useRoute()
const router = useRouter()

const isLoggedIn = ref(false)
const username = ref('')
const userCallsign = ref('')
const userNickname = ref('')
const userAvatar = ref('/assets/images/shortcut.svg')
const logoUrl = ref('/assets/images/chinaflier.bmp')
const showUserMenu = ref(false)
const isSidebarVisible = ref(false)
const isAcademyMenuOpen = ref(false)
const isCareerMenuOpen = ref(false)
const isAdminMenuOpen = ref(false)
const isEventsMenuOpen = ref(false)
const userAtcLevel = ref(0)
const userIsIns = ref(0)
const userLevelId = ref(0)
const unreadCount = ref(0)
const pilotPermissions = ref<any>(null)
const userInstructor = ref(false)

// 用户权限级别计算属性
const userAtcLevelComputed = computed(() => {
  return userAtcLevel.value
})

// 用户level_id计算属性
const userLevelIdComputed = computed(() => {
  return userLevelId.value
})

// 登录状态计算属性，确保响应性
const isLoggedInComputed = computed(() => {
  const pilotToken = localStorage.getItem('pilot_token')
  const tokenExpires = localStorage.getItem('pilot_token_expires')
  const pilotUser = localStorage.getItem('pilot_user')
  
  if (pilotToken && tokenExpires && pilotUser) {
    const expiresAt = new Date(tokenExpires)
    if (expiresAt > new Date()) {
      return true
    }
  }
  return false
})

// 判断是否在空管学院相关页面
const isInAcademySection = computed(() => {
  return route.path.startsWith('/academy')
})

// 判断是否在职业生涯相关页面
const isInCareerSection = computed(() => {
  return route.path.startsWith('/career')
})

// 判断是否在管理相关页面
const isInAdminSection = computed(() => {
  return route.path.startsWith('/admin')
})

// 判断是否在活动相关页面
const isInEventsSection = computed(() => {
  return route.path.startsWith('/events')
})

// 显示名称计算属性
const displayName = computed(() => {
  if (userNickname.value && userCallsign.value) {
    if (userNickname.value === userCallsign.value) {
      return userCallsign.value
    }
    return `${userNickname.value}[${userCallsign.value}]`
  } else if (userNickname.value) {
    return userNickname.value
  } else if (userCallsign.value) {
    return userCallsign.value
  }
  return '飞行员'
})

// 获取未读消息数量
const getUnreadCount = async () => {
  try {
    const response = await pilotAuthAPI.getUnreadMessageCount() as any
    
    if (response && response.success) {
      unreadCount.value = response.data.unread_count || 0
    }
  } catch (error) {
    console.error('获取未读消息数量错误:', error)
  }
}

// 检查登录状态
const checkLoginStatus = () => {
  const pilotToken = localStorage.getItem('pilot_token')
  const tokenExpires = localStorage.getItem('pilot_token_expires')
  const pilotUser = localStorage.getItem('pilot_user')
  const pilotPermissionsStr = localStorage.getItem('pilot_permissions')

  if (pilotToken && tokenExpires && pilotUser) {
    // 检查token是否过期
    const expiresAt = new Date(tokenExpires)
    if (expiresAt > new Date()) {
      const user = JSON.parse(pilotUser)
      isLoggedIn.value = true
      userNickname.value = user.callsign || ''
      userCallsign.value = user.callsign || ''
      userAtcLevel.value = user.atc_level || 0
      userLevelId.value = user.level_id || 0
      // 动态生成chinaflier头像链接
              userAvatar.value = user.uid ? `https://pilot.chinaflier.com/api/v1/data/avatar?pic=true&uid=${user.uid}` : '/assets/images/shortcut.svg'
      userIsIns.value = user.is_ins || 0
      unreadCount.value = user.unread_count || 0

      // 如果 localStorage.pilot_permissions 已存在，直接使用，避免等待 API
      if (pilotPermissionsStr) {
        try {
          const savedPermissions = JSON.parse(pilotPermissionsStr)
          pilotPermissions.value = savedPermissions
          // 检查教员权限
          checkInstructorPermission()
          console.log('从 localStorage 加载权限:', savedPermissions)
        } catch (e) {
          console.error('解析 localStorage.pilot_permissions 失败:', e)
        }
      }

      return true
    } else {
      // token过期，清除本地存储
      clearLoginData()
    }
  }

  isLoggedIn.value = false
  userAtcLevel.value = 0
  userIsIns.value = 0
  pilotPermissions.value = null
  userInstructor.value = false
  return false
}

// 清除登录数据
const clearLoginData = () => {
  localStorage.removeItem('pilot_token')
  localStorage.removeItem('pilot_user')
  localStorage.removeItem('pilot_token_expires')
  localStorage.removeItem('pilot_permissions')
  isLoggedIn.value = false
  userNickname.value = ''
  userCallsign.value = ''
  userAtcLevel.value = 0
  userLevelId.value = 0
  userIsIns.value = 0
  unreadCount.value = 0
  pilotPermissions.value = null
  userInstructor.value = false
}

// 检查教员权限（从 localStorage.pilot_permissions）
const checkInstructorPermission = () => {
  // 检查 localStorage.pilot_permissions 中的 is_instructor 标志
  const pilotPermissionsStr = localStorage.getItem('pilot_permissions')
  if (pilotPermissionsStr) {
    try {
      const savedPermissions = JSON.parse(pilotPermissionsStr)
      userInstructor.value = savedPermissions.is_instructor === true
    } catch (e) {
      userInstructor.value = false
    }
  } else {
    userInstructor.value = false
  }
}

// 拉取pilot_permissions
const fetchPilotPermissions = async () => {
  if (!userCallsign.value) return
  try {
    const res = await getPilotPermissionsByUidOrCallsign({ callsign: userCallsign.value })
    if (res.success && res.data && res.data.permissions) {
      pilotPermissions.value = {}
      let hasInstructor = false
      for (const perm of res.data.permissions) {
        // 管理员权限
        if (perm.main_permission === '管理员权限') {
          if (perm.sub_permission === '管理员') pilotPermissions.value.admin_admin = perm.value
          if (perm.sub_permission === '活动管理员') pilotPermissions.value.admin_event = perm.value
          if (perm.sub_permission === '人员管理员') pilotPermissions.value.admin_member = perm.value
          if (perm.sub_permission === '系统管理员') pilotPermissions.value.admin_system = perm.value
        }
        // 教员权限
        if (
          (perm.main_permission === '教员权限' && perm.sub_permission === '教员主任' && perm.value) ||
          ((perm.main_permission === '塔台权限' || perm.main_permission === '终端权限' || perm.main_permission === '区域权限') && perm.sub_permission === '教员' && perm.value)
        ) {
          hasInstructor = true
        }
      }
      userInstructor.value = hasInstructor
      // 同时保存教员权限标志到 pilotPermissions
      pilotPermissions.value.is_instructor = hasInstructor

      // 控制台输出
      const perms = []
      if (pilotPermissions.value.admin_admin) perms.push('管理员')
      if (pilotPermissions.value.admin_event) perms.push('活动管理员')
      if (pilotPermissions.value.admin_member) perms.push('人员管理员')
      if (pilotPermissions.value.admin_system) perms.push('系统管理员')
      if (userInstructor.value) perms.push('教员')
      console.log(`当前用户拥有的管理权限: ${perms.length > 0 ? perms.join('、') : '无'}`)

      // 保存权限信息到localStorage，供路由守卫使用（包含教员权限标志）
      localStorage.setItem('pilot_permissions', JSON.stringify(pilotPermissions.value))
      console.log('权限信息已保存到localStorage:', pilotPermissions.value)
    }
  } catch (e) {
    pilotPermissions.value = null
    userInstructor.value = false
  }
}

// 管理权限计算属性
const userAdmin = computed(() => {
  const hasAdmin = pilotPermissions.value?.admin_admin === 1
  console.log('userAdmin检查:', { 
    pilotPermissions: pilotPermissions.value, 
    admin_admin: pilotPermissions.value?.admin_admin, 
    hasAdmin 
  })
  return hasAdmin
})
const userEventAdmin = computed(() => {
  // 只需要活动管理权限或系统管理权限即可，不需要基础管理员权限
  const hasEventAdmin = pilotPermissions.value?.admin_event === 1 || pilotPermissions.value?.admin_system === 1
  console.log('userEventAdmin检查:', {
    admin_event: pilotPermissions.value?.admin_event,
    admin_system: pilotPermissions.value?.admin_system,
    hasEventAdmin
  })
  return hasEventAdmin
})
const userMemberAdmin = computed(() => {
  // 只需要人员管理权限或系统管理权限即可
  const hasMemberAdmin = pilotPermissions.value?.admin_member === 1 || pilotPermissions.value?.admin_system === 1
  console.log('userMemberAdmin检查:', {
    admin_member: pilotPermissions.value?.admin_member,
    admin_system: pilotPermissions.value?.admin_system,
    hasMemberAdmin
  })
  return hasMemberAdmin
})
const userSystemAdmin = computed(() => {
  // 只需要系统管理权限即可
  const hasSystemAdmin = pilotPermissions.value?.admin_system === 1
  console.log('userSystemAdmin检查:', {
    admin_system: pilotPermissions.value?.admin_system,
    hasSystemAdmin
  })
  return hasSystemAdmin
})

// 登录后拉取权限
watch([isLoggedIn, userCallsign], ([login, callsign]) => {
  if (login && callsign) {
    fetchPilotPermissions()
    // 连接 WebSocket
    const pilotToken = localStorage.getItem('pilot_token')
    if (pilotToken) {
      websocketClient.connect(pilotToken)
    }
  }
})

// WebSocket 权限变更事件处理
const handlePermissionChanged = async (data: any) => {
  console.log('📢 处理权限变更通知:', data)

  // 更新 localStorage.pilot_user
  const pilotUserStr = localStorage.getItem('pilot_user')
  if (pilotUserStr) {
    const pilotUser = JSON.parse(pilotUserStr)
    pilotUser.atc_level = data.new_atc_level
    pilotUser.is_ins = data.new_is_ins
    localStorage.setItem('pilot_user', JSON.stringify(pilotUser))

    // 更新本地响应式变量
    userAtcLevel.value = data.new_atc_level
    userIsIns.value = data.new_is_ins
  }

  // 重新获取完整权限信息
  if (userCallsign.value) {
    try {
      const res = await getPilotPermissionsByUidOrCallsign({ callsign: userCallsign.value })
      if (res.success && res.data && res.data.permissions) {
        const permissions: Record<string, any> = {}
        let hasInstructor = false
        for (const perm of res.data.permissions) {
          if (perm.main_permission === '管理员权限') {
            if (perm.sub_permission === '管理员') permissions.admin_admin = perm.value
            if (perm.sub_permission === '活动管理员') permissions.admin_event = perm.value
            if (perm.sub_permission === '人员管理员') permissions.admin_member = perm.value
            if (perm.sub_permission === '系统管理员') permissions.admin_system = perm.value
          }
          // 检查教员权限
          if (
            (perm.main_permission === '教员权限' && perm.sub_permission === '教员主任' && perm.value) ||
            ((perm.main_permission === '塔台权限' || perm.main_permission === '终端权限' || perm.main_permission === '区域权限') && perm.sub_permission === '教员' && perm.value)
          ) {
            hasInstructor = true
          }
        }
        // 保存教员权限标志
        permissions.is_instructor = hasInstructor
        localStorage.setItem('pilot_permissions', JSON.stringify(permissions))
        pilotPermissions.value = permissions
        userInstructor.value = hasInstructor
      }
    } catch (e) {
      console.error('重新获取权限失败:', e)
    }
  }

  // 提示用户
  ElMessageBox.alert(
    `您的权限已被 ${data.operator_name} 更新。\n\n变更内容：${data.change_detail}\n\n新等级：${data.new_atc_level}`,
    '权限变更通知',
    {
      confirmButtonText: '刷新页面',
      type: 'warning',
      callback: () => {
        window.location.reload()
      }
    }
  )
}

// 注册 WebSocket 事件监听
onMounted(() => {
  websocketClient.on('permission_changed', handlePermissionChanged)
})

// 监听路由变化，自动展开空管学院菜单
watch(() => route.path, (newPath) => {
  // 每次路由变化时重新检查登录状态
  checkLoginStatus()
  
  // 如果是教员，重新获取未读消息数量
  if (userIsIns.value === 1) {
    getUnreadCount()
  }
  
  if (newPath.startsWith('/academy')) {
    isAcademyMenuOpen.value = true
  }
  if (newPath.startsWith('/career')) {
    isCareerMenuOpen.value = true
  }
  if (newPath.startsWith('/admin')) {
    isAdminMenuOpen.value = true
  }
  if (newPath.startsWith('/events')) {
    isEventsMenuOpen.value = true
  }
  
  // 当从登录页面返回时，重新检查登录状态
  if (newPath === '/' && route.query.from === 'login') {
    checkLoginStatus()
  }
  
  // 当访问首页时，也重新检查登录状态
  if (newPath === '/') {
    checkLoginStatus()
  }
})

// 处理登录
const handleLogin = () => {
  router.push('/login')
}

// 处理退出登录
const handleLogout = async () => {
  try {
    // 调用退出登录API
    await pilotAuthAPI.logout()
    ElMessage.success('退出登录成功')
  } catch (error) {
    console.error('退出登录错误:', error)
  } finally {
    // 清除本地数据
    clearLoginData()
    showUserMenu.value = false
    
    // 跳转到首页
    router.push('/')
  }
}

const toggleUserMenu = () => { 
  console.log('切换用户菜单，当前状态:', showUserMenu.value)
  showUserMenu.value = !showUserMenu.value 
  console.log('切换后状态:', showUserMenu.value)
}

const toggleSidebar = () => { 
  isSidebarVisible.value = !isSidebarVisible.value 
}

const toggleAcademyMenu = () => { 
  isAcademyMenuOpen.value = !isAcademyMenuOpen.value 
}

const toggleCareerMenu = () => { 
  isCareerMenuOpen.value = !isCareerMenuOpen.value 
}

const toggleAdminMenu = () => { 
  isAdminMenuOpen.value = !isAdminMenuOpen.value 
}

const toggleEventsMenu = () => { 
  isEventsMenuOpen.value = !isEventsMenuOpen.value 
}

const goToProfile = () => {
  router.push('/profile')
}

const goToSettings = () => {
  router.push('/settings')
}

// 组件挂载时检查登录状态
onMounted(() => {
  checkLoginStatus()
  
  // 如果是教员，获取一次未读消息数量
  if (userIsIns.value === 1) {
    getUnreadCount()
  }
  
  // 立即检查申请页面状态
  if (route.name === 'application') {
    const style = document.createElement('style')
    style.id = 'application-mode-style'
    style.textContent = `
      .app-wrapper, .public-container {
        display: none !important;
      }
      .application-only-container {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        z-index: 99999 !important;
        background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%) !important;
      }
      /* 确保ElMessage能够正常显示 */
      .el-message {
        z-index: 999999 !important;
      }
      .el-message-box__wrapper {
        z-index: 999999 !important;
      }
    `
    document.head.appendChild(style)
  }
  
  // 添加定时检查，确保状态及时更新
  setInterval(() => {
    checkLoginStatus()
  }, 300000) // 每5分钟检查一次
})

// 组件卸载时清理
onUnmounted(() => {
  // 移除 WebSocket 事件监听
  websocketClient.off('permission_changed', handlePermissionChanged)

  // 移除强制样式
  const style = document.getElementById('application-mode-style')
  if (style) {
    style.remove()
  }
})

// 申请页面强制隐藏其他元素的样式
const applicationMode = computed(() => route.name === 'application')

// 监听申请页面状态，强制隐藏其他元素
watch(applicationMode, (isApplication) => {
  if (isApplication) {
    // 强制隐藏所有其他元素
    const style = document.createElement('style')
    style.id = 'application-mode-style'
    style.textContent = `
      .app-wrapper, .public-container {
        display: none !important;
      }
      .application-only-container {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        z-index: 99999 !important;
        background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%) !important;
      }
      /* 确保ElMessage能够正常显示 */
      .el-message {
        z-index: 999999 !important;
      }
      .el-message-box__wrapper {
        z-index: 999999 !important;
      }
    `
    document.head.appendChild(style)
  } else {
    // 移除强制样式
    const style = document.getElementById('application-mode-style')
    if (style) {
      style.remove()
    }
  }
})

const closeSidebar = () => {
  isSidebarVisible.value = false
}

// 维护模式重试连接
const retryStatus = ref('')
const retrySuccess = ref(false)
const retrying = ref(false)

const retryConnection = async () => {
  retrying.value = true
  retryStatus.value = ''
  try {
    const response = await fetch('/api/health')
    const data = await response.json()
    if (data.success) {
      retrySuccess.value = true
      retryStatus.value = '系统已恢复，即将刷新页面...'
      setMaintenanceMode(false)
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    } else {
      retrySuccess.value = false
      retryStatus.value = data.message || '系统仍在维护中，请耐心等待'
    }
  } catch (error) {
    retrySuccess.value = false
    retryStatus.value = '无法连接到服务器，请检查网络后重试'
  } finally {
    retrying.value = false
  }
}
</script>

<style lang="scss">
// 全局样式
html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  background-color: #f5f7fa;
}

#app {
  height: 100%;
}

// 登录专用容器样式
.login-only-container {
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
}

// 公开访问容器样式
.public-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f5f7fa;
}

// 申请专用容器样式
.application-only-container {
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  overflow: hidden;
}

.app-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f5f7fa;
}

// 侧边栏样式
.sidebar {
  width: 250px;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  background-color: #fff;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;

  @media (max-width: 991.98px) {
    transform: translateX(-100%);
    
    &.sidebar-visible {
      transform: translateX(0);
    }
  }
}

.sidebar-header {
  padding: 20px;
  border-bottom: 1px solid #ebeef5;
  
  .sidebar-header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  
  .logo {
    display: flex;
    align-items: center;
    text-decoration: none;
    
    .logo-img {
      height: 40px;
      width: auto;
    }
  }
  
  .sidebar-close {
    background: none;
    border: none;
    color: #606266;
    cursor: pointer;
    padding: 8px;
    border-radius: 4px;
    transition: all 0.3s ease;
    
    &:hover {
      background-color: #f5f7fa;
      color: #409eff;
    }
    
    i {
      font-size: 18px;
    }
  }
}

.sidebar-nav {
  flex: 1;
  padding: 20px 0;
  overflow-y: auto;

  .nav-item {
    display: flex;
    flex-direction: column;
    color: #606266;
    text-decoration: none;
    transition: all 0.3s ease;
    cursor: pointer;
    position: relative;

    .nav-item-content {
      display: flex;
      align-items: center;
      padding: 12px 20px;
      transition: all 0.3s ease;

      &:hover {
        background-color: #f5f7fa;
        color: #409eff;
      }

      i {
        margin-right: 10px;
        width: 20px;
        text-align: center;
        transition: transform 0.3s ease;
      }

      .submenu-arrow {
        margin-left: auto;
        transition: transform 0.3s ease;
        
        &.rotated {
          transform: rotate(180deg);
        }
      }
    }

    &.active {
      .nav-item-content {
        background-color: #ecf5ff;
        color: #409eff;
        font-weight: 500;
        position: relative;

        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background-color: #409eff;
        }
      }
    }

    &.has-submenu {
      &.active {
        > .nav-item-content {
          background-color: #ecf5ff;
          color: #409eff;
          font-weight: 500;
          position: relative;

          &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 4px;
            background-color: #409eff;
          }
        }
      }
    }

    .submenu {
      background-color: #fafafa;
      overflow: hidden;
      transition: max-height 0.3s ease-in-out;
      max-height: 0;

      &.show {
        max-height: 500px; // 根据实际内容调整
      }

      .submenu-item {
        display: flex;
        align-items: center;
        padding: 10px 20px 10px 50px;
        color: #606266;
        text-decoration: none;
        transition: all 0.3s ease;
        font-size: 0.95em;
        position: relative;

        &:hover {
          background-color: #f0f0f0;
          color: #409eff;
        }

        &.active {
          background-color: #e6f1fc;
          color: #409eff;
          font-weight: 500;

          &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 4px;
            background-color: #409eff;
          }
        }

        i {
          margin-right: 8px;
          width: 16px;
          text-align: center;
        }
      }
    }
  }
}

// 主内容区样式
.main-content {
  flex: 1;
  margin-left: 250px;
  min-height: 100vh;
  background-color: #f5f7fa;
  padding-bottom: 40px;

  @media (max-width: 991.98px) {
    margin-left: 0;
  }
}

// 顶部导航栏样式
.top-header {
  height: 60px;
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  position: sticky;
  top: 0;
  z-index: 999;
  margin-bottom: 20px;

  .menu-toggle {
    background: none;
    border: none;
    color: #606266;
    font-size: 1.2rem;
    cursor: pointer;
    padding: 5px;
    
    &:hover {
      color: #409eff;
    }
  }

  .login-btn-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background-color: #409eff;
    color: #fff;
    text-decoration: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.3s ease;
    
    &:hover {
      background-color: #66b1ff;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
    }
    
    i {
      font-size: 14px;
    }
  }
}

// 用户菜单样式
.user-menu {
  position: relative;
  
  .user-info {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 5px 10px;
    border-radius: 4px;
    transition: background-color 0.3s ease;
    
    &:hover {
      background-color: #f5f7fa;
    }
    
    .user-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      margin-right: 8px;
    }
    
    .username {
      margin-right: 8px;
      color: #606266;
    }
  }
  
  .user-dropdown {
    position: absolute;
    bottom: 100%;
    left: 0;
    right: 0;
    background-color: #fff;
    border-radius: 4px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    margin-bottom: 5px;
    z-index: 1000;
    border: 1px solid #ebeef5;
    
    .dropdown-item {
      display: flex;
      align-items: center;
      padding: 10px 15px;
      color: #606266;
      text-decoration: none;
      transition: all 0.3s ease;
      cursor: pointer;
      
      i {
        margin-right: 8px;
        width: 16px;
        text-align: center;
      }
      
      &:hover {
        background-color: #f5f7fa;
        color: #409eff;
      }
      
      &.logout-item {
        color: #f56c6c;
        
        &:hover {
          background-color: #fef0f0;
          color: #f56c6c;
        }
      }
    }
    
    .dropdown-divider {
      height: 1px;
      background-color: #ebeef5;
      margin: 5px 0;
    }
  }
}

// 登录按钮样式
.login-section {
  padding: 20px;
  border-top: 1px solid #ebeef5;
  
  .login-btn {
    width: 100%;
    padding: 10px;
    background-color: #409eff;
    color: #fff;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.3s ease;
    
    i {
      margin-right: 8px;
    }
    
    &:hover {
      background-color: #66b1ff;
    }
  }
}

// 未读消息徽章样式
.unread-badge {
  margin-left: 8px;
  
  :deep(.el-badge__content) {
    background-color: #e74c3c;
    border: none;
    font-size: 10px;
    height: 16px;
    line-height: 16px;
    min-width: 16px;
    padding: 0 4px;
  }
}

// 移动端遮罩层样式
.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

// 维护模式覆盖层样式
.maintenance-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  backdrop-filter: blur(4px);
}

.maintenance-card {
  background: #fff;
  border-radius: 16px;
  padding: 48px 40px;
  text-align: center;
  max-width: 480px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.maintenance-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  background: linear-gradient(135deg, #f0f5ff 0%, #e6f0ff 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 36px;
    color: #409eff;
  }
}

.maintenance-title {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 12px;
}

.maintenance-message {
  font-size: 15px;
  color: #606266;
  margin: 0 0 8px;
  line-height: 1.6;
}

.maintenance-hint {
  font-size: 13px;
  color: #909399;
  margin: 0 0 28px;
}

.maintenance-retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 28px;
  background: #409eff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #66b1ff;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
  }

  i {
    font-size: 14px;
  }
}

.maintenance-retry-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 0 0 20px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;

  i {
    font-size: 16px;
  }

  &.retry-success {
    background: #f0f9eb;
    color: #67c23a;
    border: 1px solid #e1f3d8;
  }

  &.retry-fail {
    background: #fef0f0;
    color: #f56c6c;
    border: 1px solid #fde2e2;
  }
}
</style> 